import { useState, useEffect } from "react";
import axios,{AxiosRequestHeaders} from "axios";
import { Link } from "react-router-dom";
import loaderimage from "../../images/giphy.gif";
import ConfirmationModal from "../shared/ConfirmationModal";
import NewconfirmationModal from "../shared/NewconfirmationModal";
import AllFailureModal from "../shared/AllFailureModal";

const CandidateList = () => {
  const [candidates, setCandidateList] = useState<any[]>([]);
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [failureMessage, setFailureMessage] = useState('');
  const [modalShow, setModalShow] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [pendingAction, setPendingAction] = useState<any>(null);
  const [isConfirmModal, setIsConfirmModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCandidates, setFilteredCandidates] = useState<any[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  const headers: AxiosRequestHeaders = {
    'x-access-token': localStorage.getItem('jwttoken') || '',
  };
  const callCandidateListAPI = async (page = 1) => {
    try {
      setLoader(true);
      setError("");
      const url = `${process.env.REACT_APP_API_URL}candidate_details/allcandidate?page=${page}&limit=100`;
      const response = await axios.get(url, { headers });
      setCandidateList(response.data.candidates || response.data);
      setFilteredCandidates(response.data.candidates || response.data);
      setCurrentPage(response.data.currentPage || page);
      setTotalPages(response.data.totalPages || 1);
      setTotalCandidates(response.data.totalCandidates || (response.data.length || 0));
    } catch (error) {
      setError("Not Found!!!");
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    callCandidateListAPI();
  }, []);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (searchTerm === "") {
        setFilteredCandidates(candidates);
      } else {
        const filtered = candidates.filter((candidate) =>
          [
            candidate.userid?.toString().toLowerCase(),
            candidate.city?.toLowerCase(),
            candidate.college?.toLowerCase(),
            candidate.whatsapp_mobile?.toLowerCase(),
          ].some((value) => value?.includes(searchTerm))
        );
        setFilteredCandidates(filtered);
      }
    }, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, candidates]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const handleConfirmation = async (isConfirmed: boolean) => {
    setModalShow(false);
    if (isConfirmed && pendingAction) {
      try {
        const url =
          process.env.REACT_APP_API_URL + `candidate_details/deletecandidate_details/${pendingAction}`;
        await axios.delete(url);
        setSuccessMessage("Candidate deleted successfully!");
        setShowSuccessModal(true);
        callCandidateListAPI(currentPage);
      } catch (error) {
        console.error("Error deleting Candidate:", error);
        setFailureMessage("Failed to delete candidate. Please try again.");
        setShowFailureModal(true);
      }
    }
  };

  const confirmDelete = (id: any) => {
    setModalMessage("Are you sure you want to delete this candidate?");
    setPendingAction(id);
    setIsConfirmModal(true);
    setModalShow(true);
  };

  const closeModal = () => {
    setModalShow(false);
    if (!isConfirmModal) {
      callCandidateListAPI(currentPage);
    }
  };

  const handlePageChange = (page: number) => {
    callCandidateListAPI(page);
  };

  const startEntry = (currentPage - 1) * 100 + 1;
  const endEntry = Math.min(currentPage * 100, totalCandidates);
  
  return (
    <div className="container-fluid p-0">
      <div className="row g-0">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="text-black fw-bold">Candidate Management</h2>
            <div className="d-flex gap-2 align-items-center w-50">
              <span className="fw-bold text-secondary text-nowrap">{totalCandidates} results</span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by User ID, College, City, or Mobile"
                value={searchTerm}
                onChange={handleSearch}
              />
              <Link to="/candidateadd" className="btn btn-info text-white">
                + Add Candidate
              </Link>
            </div>
          </div>
  
          <div className="card shadow-sm border-0 rounded" style={{ maxHeight: 'calc(100vh - 250px)' }}>
            <div className="card-header bg-info text-white">
              <h5 className="mb-0">Candidate List</h5>
            </div>
            <div className="card-body p-0 position-relative">
              <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light position-sticky top-0">
                    <tr>
                      <th>#</th>
                      <th>User ID</th>
                      <th>City</th>
                      <th>Hometown</th>
                      <th>College</th>
                      <th>University</th>
                      <th>Passing Year</th>
                      <th>Branch</th>
                      <th>Whatsapp Number</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loader ? (
                      <tr>
                        <td colSpan={10} className="text-center py-2">
                          <img src={loaderimage} alt="Loading..." height="40" />
                        </td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan={10} className="text-center text-danger fw-bold py-3">
                          <div className="alert alert-danger text-center m-3" role="alert">
                            Oops! Something went wrong. Please try again later.
                          </div>
                        </td>
                      </tr>
                    ) : filteredCandidates.length > 0 ? (
                      filteredCandidates.map((candidate, index) => (
                        <tr key={index} className="border-bottom">
                          <td>{startEntry + index}</td>
                          <td>{candidate.userid}</td>
                          <td>{candidate.city}</td>
                          <td>{candidate.hometown}</td>
                          <td>{candidate.college}</td>
                          <td>{candidate.university}</td>
                          <td>{candidate.passing_year}</td>
                          <td>{candidate.branch}</td>
                          <td>{candidate.whatsapp_mobile}</td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-2">
                              <Link
                                to={`/candidate-details/${candidate.userid}`}
                                className="btn btn-outline-secondary btn-sm"
                              >
                                View
                              </Link>
                              <Link
                                to={`/candidate_update/${candidate.userid}`}
                                className="btn btn-outline-warning btn-sm"
                              >
                                Edit
                              </Link>
                              <button
                                onClick={() => confirmDelete(candidate.userid)}
                                className="btn btn-outline-primary btn-sm"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={10} className="text-center text-muted fw-bold">
                          <div className="alert alert-secondary text-center m-3" role="alert">
                            No candidates List available
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
  
              {/* Pagination */}
              <div className="position-sticky bottom-0 bg-white py-2 border-top">
            <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted ps-3">
                    Showing {startEntry}-{endEntry} of {totalCandidates} entries
                  </div>
                  <nav>
                    <ul className="pagination mb-0 pe-3">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, index) => (
                        <li 
                          key={index} 
                          className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                        >
                          <button 
                            className="page-link" 
                            onClick={() => handlePageChange(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
  
          <ConfirmationModal
            show={modalShow}
            modalMessage={modalMessage}
            onHide={closeModal}
            confirmation={isConfirmModal ? handleConfirmation : null}
            operationType={isConfirmModal ? "Delete" : "Ok"}
          />
          <NewconfirmationModal 
            show={showSuccessModal} 
            onHide={() => setShowSuccessModal(false)} 
            modalMessage={successMessage} 
            operationType="OK" 
            timeoutDuration={3000} 
          />
          <AllFailureModal 
            show={showFailureModal} 
            onHide={() => setShowFailureModal(false)} 
            modalMessage={failureMessage}  
            operationType="OK" 
            timeoutDuration={3000}
          />
        </div>
      </div>
    </div>
  );
};

export default CandidateList;